import * as React from 'react';
import { getDateFromString, getFormatDateFromStoreDate, getFormatDateFromObject } from 'shared-components';
import {
  BiometricDocumentList,
  DocAccessType,
  IDocumentsList,
  StudentVisaDocList,
  StudentVisaMap,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import { SummaryRow } from '../../../Summary/SummaryRow';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { boolToYesNo } from '../../../../utilities/formatting';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { getVisaLabelFromValue } from 'shared-components';
interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: StudentVisaMap;
  warning?: string;
  userRole?: UserRole;

  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;

  onResume: (url: string) => void;

  onVisaUploadClick: (visaKey: string, docKey: string) => void;
  onVisaOpenClick: (visaKey: string, docKey: string) => void;
  onVisaApproveClick: (visaKey: string, docKey: string) => void;
  onVisaRejectClick: (visaKey: string, docKey: string) => void;
  onVisaRescindRejectClick: (visaKey: string, docKey: string) => void;
  onVisaDeleteClick: (visaKey: string, docKey: string) => void;
  onVisaAcknowledgeClick: (visaKey: string, docKey: string) => void;

  onCasUploadClick: (casKey: string, docKey: string) => void;
  onCasOpenClick: (casKey: string, docKey: string) => void;
  onCasApproveClick: (casKey: string, docKey: string) => void;
  onCasRejectClick: (casKey: string, docKey: string) => void;
  onCasRescindRejectClick: (casKey: string, docKey: string) => void;
  onCasDeleteClick: (casKey: string, docKey: string) => void;
  onCasAcknowledgeClick: (casKey: string, docKey: string) => void;

  onBiometricUploadClick: (bioKey: string, docKey: string) => void;
  onBiometricOpenClick: (bioKey: string, docKey: string) => void;
  onBiometricApproveClick: (bioKey: string, docKey: string) => void;
  onBiometricRejectClick: (bioKey: string, docKey: string) => void;
  onBiometricRescindRejectClick: (bioKey: string, docKey: string) => void;
  onBiometricDeleteClick: (bioKey: string, docKey: string) => void;
  onBiometricAcknowledgeClick: (bioKey: string, docKey: string) => void;
}

const StudyVisa = (props: IProps) => {
  const { documents: visas = {}, infoOnly, userAccess, userRole } = props;

  return (
    <>
      {Object.entries(visas).map(([visasKey, studyVisa]) => {
        let hasDocs = false;
        let hasDeletedDocs = false;
        const docNodes: React.ReactNode[] = [];
        let visaType = '';
        let year = '';

        if (studyVisa.visa) {
          const visibleDocs = getVisible({
            documentsHash: studyVisa.visa,
            userAccess,
            infoOnly,
            userRole,
          }) as StudentVisaDocList[];
          if (hasDeleted(studyVisa.visa)) {
            hasDeletedDocs = true;
          }
          if (visibleDocs.length > 0) {
            hasDocs = true;
            docNodes.push(
              ...visibleDocs.map((studentVisa: StudentVisaDocList, studentVisaIndex: number) => {
                const { key: docKey, item } = studentVisa;
                visaType = item.visaType;
                year = item.year;
                return (
                  <FingerWithApprove
                    key={`study-visa-${docKey}`}
                    status={getFingerStatus(item)}
                    isProcessingPdf={item.isProcessingPdf}
                    reason={item.reason}
                    fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                    fileNames={item.fileNames}
                    label={`Previous Student Visa / eVisa ${
                      Object.keys(studyVisa.visa ?? {}).length > 1
                        ? studentVisaIndex > 0
                          ? studentVisaIndex + 1
                          : ''
                        : ''
                    }`}
                    canUpload={infoOnly ? false : props.canUpload}
                    canOpen={infoOnly ? false : props.canOpen}
                    canApprove={infoOnly ? false : props.canApprove}
                    canReject={infoOnly ? false : props.canReject}
                    isDeleting={infoOnly ? false : props.isDeleting}
                    canAcknowledge={infoOnly ? props.canAcknowledge : false}
                    onUploadClick={() => props.onVisaUploadClick(visasKey, docKey)}
                    onOpenClick={() => props.onVisaOpenClick(visasKey, docKey)}
                    onApproveClick={() => props.onVisaApproveClick(visasKey, docKey)}
                    onRejectClick={() => props.onVisaRejectClick(visasKey, docKey)}
                    onRescindRejectClick={() => props.onVisaRescindRejectClick(visasKey, docKey)}
                    onDeleteClick={() => props.onVisaDeleteClick(visasKey, docKey)}
                    onAcknowledgeClick={() => props.onVisaAcknowledgeClick(visasKey, docKey)}
                    userRole={props.userRole}
                    deletedByTTL={item.deletedByTTL}
                  >
                    {item.startDate && item.endDate ? (
                      <>
                        <SummaryRow
                          key={`student-visa-start-date-${docKey}`}
                          label="Start Date"
                          fullLabel="Enter the start date for this visa (as stated on the visa itself)"
                          value={`${getFormatDateFromObject(getDateFromString(item.startDate))}`}
                        />

                        <SummaryRow
                          key={`student-visa-end-date-${docKey}`}
                          label="End Date"
                          fullLabel="Enter the end date for this visa (as stated on the visa itself)"
                          value={`${getFormatDateFromObject(getDateFromString(item.endDate))}`}
                        />
                      </>
                    ) : undefined}
                  </FingerWithApprove>
                );
              }),
            );
          }
        }

        if (studyVisa.cas) {
          const visibleDocs = getVisible({
            documentsHash: studyVisa.cas,
            userAccess,
            infoOnly,
            userRole,
          }) as IDocumentsList[];
          if (hasDeleted(studyVisa.cas)) {
            hasDeletedDocs = true;
          }
          if (visibleDocs.length > 0) {
            hasDocs = true;
            docNodes.push(
              ...visibleDocs.map((cas: IDocumentsList, index: number) => {
                const { key: docKey, item } = cas;
                return (
                  <FingerWithApprove
                    key={`cas-document-${docKey}`}
                    status={getFingerStatus(item)}
                    isProcessingPdf={item.isProcessingPdf}
                    reason={item.reason}
                    fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                    fileNames={item.fileNames}
                    label={`Previous CAS Document ${
                      Object.keys(studyVisa.cas ?? {}).length > 1 ? (index > 0 ? index + 1 : '') : ''
                    }`}
                    canUpload={infoOnly ? false : props.canUpload}
                    canOpen={infoOnly ? false : props.canOpen}
                    canApprove={infoOnly ? false : props.canApprove}
                    canReject={infoOnly ? false : props.canReject}
                    isDeleting={infoOnly ? false : props.isDeleting}
                    canAcknowledge={infoOnly ? props.canAcknowledge : false}
                    onUploadClick={() => props.onCasUploadClick(visasKey, docKey)}
                    onOpenClick={() => props.onCasOpenClick(visasKey, docKey)}
                    onApproveClick={() => props.onCasApproveClick(visasKey, docKey)}
                    onRejectClick={() => props.onCasRejectClick(visasKey, docKey)}
                    onRescindRejectClick={() => props.onCasRescindRejectClick(visasKey, docKey)}
                    onDeleteClick={() => props.onCasDeleteClick(visasKey, docKey)}
                    onAcknowledgeClick={() => props.onCasAcknowledgeClick(visasKey, docKey)}
                    userRole={props.userRole}
                    deletedByTTL={item.deletedByTTL}
                  />
                );
              }),
            );
          }
        }

        if (studyVisa.biometric) {
          const visibleDocs = getVisible({
            documentsHash: studyVisa.biometric,
            userAccess,
            infoOnly,
            userRole,
          }) as BiometricDocumentList[];
          if (hasDeleted(studyVisa.biometric)) {
            hasDeletedDocs = true;
          }
          if (visibleDocs.length > 0) {
            hasDocs = true;
            docNodes.push(
              ...visibleDocs.map((bio: BiometricDocumentList, index: number) => {
                const { key: docKey, item } = bio;
                return (
                  <FingerWithApprove
                    id={`${docKey}-${visasKey}`}
                    key={`biometric-${docKey}`}
                    status={getFingerStatus(item)}
                    reason={item.reason}
                    fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                    fileNames={item.fileNames}
                    label={`Previous Biometric Residence Permit ${
                      Object.keys(studyVisa.biometric ?? {}).length > 1 ? (index > 0 ? index + 1 : '') : ''
                    }`}
                    canUpload={infoOnly ? false : props.canUpload}
                    canOpen={infoOnly ? false : props.canOpen}
                    canApprove={infoOnly ? false : props.canApprove}
                    canReject={infoOnly ? false : props.canReject}
                    isDeleting={infoOnly ? false : props.isDeleting}
                    canAcknowledge={infoOnly ? props.canAcknowledge : false}
                    onUploadClick={() => props.onBiometricUploadClick(visasKey, docKey)}
                    onOpenClick={() => props.onBiometricOpenClick(visasKey, docKey)}
                    onApproveClick={() => props.onBiometricApproveClick(visasKey, docKey)}
                    onRejectClick={() => props.onBiometricRejectClick(visasKey, docKey)}
                    onRescindRejectClick={() => props.onBiometricRescindRejectClick(visasKey, docKey)}
                    onDeleteClick={() => props.onBiometricDeleteClick(visasKey, docKey)}
                    onAcknowledgeClick={() => props.onBiometricAcknowledgeClick(visasKey, docKey)}
                    userRole={props.userRole}
                    deletedByTTL={item.deletedByTTL}
                  >
                    {item.dateOfIssue && item.validUntil ? (
                      <>
                        <SummaryRow
                          key={`student-previous-bio-date-of-issue-${docKey}`}
                          label="Date of issue"
                          fullLabel="What is the date of issue?"
                          value={getFormatDateFromStoreDate(item.dateOfIssue)}
                        />

                        <SummaryRow
                          key={`student-previous-bio-valid-until-${docKey}`}
                          label="Valid until"
                          fullLabel="What is the valid until date?"
                          value={getFormatDateFromStoreDate(item.validUntil)}
                        />
                      </>
                    ) : (
                      <>
                        <SummaryRow
                          key={`student-previous-bio-received-${docKey}`}
                          label="Previous Biometric Residence Permit"
                          fullLabel="Did you receive a Biometric Residence Permit?"
                          value={`${boolToYesNo(item.hasDocument)}`}
                        />
                      </>
                    )}
                  </FingerWithApprove>
                );
              }),
            );
          }
        }

        if (!hasDocs && !hasDeletedDocs) {
          return null;
        }

        if ((infoOnly || userAccess) && !hasDocs) {
          return null;
        }

        return (
          <Section
            key={`study-visa-section-${visaType}-${year}`}
            label={`You indicated you had a ${getVisaLabelFromValue(visaType)} visa in ${year}`}
            canEdit={props.canResume}
            edit={() => {
              props.onResume('history/step-1');
            }}
            warning={props.warning}
          >
            {hasDocs ? docNodes : <NoDocuments />}
          </Section>
        );
      })}
    </>
  );
};

export default StudyVisa;
